import { applyDotsFromApi } from "../dots-currency";
import {
  buyBattlePassPremium,
  claimAllBattlePassRewards,
  claimBattlePassReward,
  fetchBattlePass,
} from "./api";
import { notifyBattlePassClaim } from "./fx";
import type { BattlePassLevelView, BattlePassPayload, BattlePassRewardView } from "./types";

export type BattlePassScreenApi = {
  refresh: () => Promise<void>;
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function rewardThumb(reward: BattlePassRewardView): HTMLElement {
  const box = el("div", "bpRewardArt");
  if (reward.imageData) {
    const img = document.createElement("img");
    img.src = reward.imageData;
    img.alt = reward.label;
    box.appendChild(img);
  } else if (reward.kind === "dots") {
    box.classList.add("bpRewardArt--dots");
    box.textContent = `${reward.dots}`;
  } else {
    box.classList.add("bpRewardArt--empty");
    box.textContent = reward.kind === "none" ? "—" : "?";
  }
  return box;
}

function rewardStateClass(state?: string): string {
  return `bpReward--${state || "locked"}`;
}

export function mountBattlePassScreen(
  root: HTMLElement,
  opts: { onClaimableChange?: (count: number) => void },
): BattlePassScreenApi {
  root.classList.add("bpScreen");
  let payload: BattlePassPayload = { active: null, claimableCount: 0 };
  let focusLevel = 1;
  let busy = false;

  const render = () => {
    root.replaceChildren();
    const active = payload.active;
    if (!active) {
      const empty = el("div", "bpEmpty");
      empty.append(
        el("h1", "bpTitle", "BATTLE PASS"),
        el("p", "accessHint", "No season is live yet. Check back after an admin publishes one."),
      );
      root.appendChild(empty);
      opts.onClaimableChange?.(0);
      return;
    }

    const player = active.player;
    focusLevel = Math.max(1, Math.min(99, focusLevel || player.level));
    const xpPct = Math.max(0, Math.min(100, Math.round((player.xp / Math.max(1, player.xpToNext)) * 100)));

    const header = el("header", "bpHeader");
    if (active.imageData) {
      const art = document.createElement("img");
      art.className = "bpSeasonArt";
      art.src = active.imageData;
      art.alt = "";
      header.appendChild(art);
    }
    const meta = el("div", "bpHeaderMeta");
    meta.append(
      el("p", "bpSeasonKicker", `SEASON ${active.seasonNumber}`),
      el("h1", "bpTitle", active.name),
    );
    if (active.description) meta.appendChild(el("p", "accessHint", active.description));
    const levelRow = el("div", "bpLevelRow");
    const badge = el("div", "bpLevelBadge");
    badge.innerHTML = `<span>LVL</span><strong>${player.level}</strong>`;
    const xpBlock = el("div", "bpXpBlock");
    const xpLabel = el("div", "bpXpLabel", `${player.xp.toLocaleString()} / ${player.xpToNext.toLocaleString()} XP`);
    const bar = el("div", "bpXpBar");
    const fill = el("div", "bpXpFill");
    fill.style.width = `${xpPct}%`;
    bar.appendChild(fill);
    xpBlock.append(xpLabel, bar);
    levelRow.append(badge, xpBlock);
    meta.appendChild(levelRow);
    header.appendChild(meta);

    const premium = el("div", "bpPremiumCard");
    if (player.premiumOwned) {
      premium.classList.add("bpPremiumCard--owned");
      premium.append(el("strong", undefined, "PREMIUM UNLOCKED"), el("p", undefined, "Claim every gold reward you already reached."));
    } else {
      premium.append(
        el("strong", undefined, "PREMIUM TRACK"),
        el("p", undefined, `Unlock gold rewards for ${active.premiumPriceDots.toLocaleString()} Dots.`),
      );
      const buy = el("button", "btn green bpBuyBtn", `BUY · ${active.premiumPriceDots.toLocaleString()} Dots`);
      buy.type = "button";
      buy.onclick = () => void onBuy();
      premium.appendChild(buy);
    }
    header.appendChild(premium);
    root.appendChild(header);

    const tools = el("div", "bpTools");
    const claimAll = el("button", "btn green", `CLAIM ALL${payload.claimableCount ? ` (${payload.claimableCount})` : ""}`);
    claimAll.type = "button";
    claimAll.disabled = payload.claimableCount < 1 || busy;
    claimAll.onclick = () => void onClaimAll();
    const jump = el("div", "bpJump");
    const jumpLabel = el("label", undefined, "Jump to");
    const jumpInput = document.createElement("input");
    jumpInput.type = "number";
    jumpInput.min = "1";
    jumpInput.max = "99";
    jumpInput.value = String(focusLevel);
    jumpInput.className = "formInput bpJumpInput";
    jumpInput.onchange = () => {
      focusLevel = Math.max(1, Math.min(99, Math.floor(Number(jumpInput.value) || 1)));
      scrollToLevel(focusLevel);
    };
    const currentBtn = el("button", "btn", "Current");
    currentBtn.type = "button";
    currentBtn.onclick = () => {
      focusLevel = Math.min(99, player.level);
      scrollToLevel(focusLevel);
    };
    jump.append(jumpLabel, jumpInput, currentBtn);
    tools.append(claimAll, jump);
    root.appendChild(tools);

    const trackWrap = el("div", "bpTrackWrap");
    const track = el("div", "bpTrack");
    track.setAttribute("role", "list");
    for (const row of active.levels) {
      track.appendChild(renderColumn(row, player.level));
    }
    trackWrap.appendChild(track);
    root.appendChild(trackWrap);

    opts.onClaimableChange?.(payload.claimableCount);
    requestAnimationFrame(() => scrollToLevel(Math.min(99, player.level), true));
  };

  function renderColumn(row: BattlePassLevelView, playerLevel: number): HTMLElement {
    const col = el("article", "bpCol");
    col.dataset.level = String(row.level);
    col.setAttribute("role", "listitem");
    if (row.level === playerLevel) col.classList.add("bpCol--current");
    col.appendChild(renderReward(row.premium, row.level, "premium"));
    const mid = el("div", "bpColLevel", String(row.level));
    col.appendChild(mid);
    col.appendChild(renderReward(row.free, row.level, "free"));
    return col;
  }

  function renderReward(reward: BattlePassRewardView, level: number, track: "free" | "premium"): HTMLElement {
    const card = el("div", `bpReward ${rewardStateClass(reward.state)} ${track === "premium" ? "bpReward--premium" : "bpReward--free"}`);
    card.appendChild(rewardThumb(reward));
    card.appendChild(el("p", "bpRewardLabel", reward.label));
    card.appendChild(el("span", "bpRewardTrack", track === "premium" ? "PREMIUM" : "FREE"));
    if (reward.claimable) {
      const btn = el("button", "btn green bpClaimBtn", "CLAIM");
      btn.type = "button";
      btn.onclick = () => void onClaim(level, track, reward);
      card.appendChild(btn);
    } else if (reward.state === "claimed") {
      card.appendChild(el("span", "bpRewardStatus", "CLAIMED"));
    } else if (reward.state === "premium_locked") {
      card.appendChild(el("span", "bpRewardStatus", "PREMIUM"));
    } else if (reward.state === "locked") {
      card.appendChild(el("span", "bpRewardStatus", "LOCKED"));
    }
    return card;
  }

  function scrollToLevel(level: number, instant = false) {
    const col = root.querySelector(`[data-level="${level}"]`);
    if (col instanceof HTMLElement) {
      col.scrollIntoView({ inline: "center", block: "nearest", behavior: instant ? "auto" : "smooth" });
    }
  }

  async function applyPayload(next: BattlePassPayload, extra?: { dots?: unknown }) {
    payload = next;
    if (extra?.dots) applyDotsFromApi(extra.dots as Parameters<typeof applyDotsFromApi>[0]);
    render();
  }

  async function onClaim(level: number, track: "free" | "premium", reward: BattlePassRewardView) {
    if (busy) return;
    busy = true;
    try {
      const res = await claimBattlePassReward(level, track);
      notifyBattlePassClaim(res.reward?.label || reward.label, res.reward?.imageData || reward.imageData);
      await applyPayload(res, res);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Could not claim reward");
    } finally {
      busy = false;
    }
  }

  async function onClaimAll() {
    if (busy) return;
    busy = true;
    try {
      const res = await claimAllBattlePassRewards();
      if (res.claimedCount) notifyBattlePassClaim(`${res.claimedCount} rewards claimed`);
      await applyPayload(res, res);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : "Could not claim rewards");
    } finally {
      busy = false;
    }
  }

  async function onBuy() {
    const price = payload.active?.premiumPriceDots ?? 0;
    if (!window.confirm(`Buy Premium for this season for ${price.toLocaleString()} Dots?`)) return;
    if (busy) return;
    busy = true;
    try {
      const res = await buyBattlePassPremium();
      notifyBattlePassClaim("Premium unlocked");
      await applyPayload(res, res);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not buy Premium";
      window.alert(msg === "insufficient_dots" ? "Not enough Dots." : msg);
    } finally {
      busy = false;
    }
  }

  return {
    refresh: async () => {
      try {
        payload = await fetchBattlePass();
      } catch {
        payload = { active: null, claimableCount: 0 };
      }
      render();
    },
  };
}

export function syncBattlePassClaimBadge(btn: HTMLElement | null, count: number): void {
  if (!btn) return;
  let badge = btn.querySelector(".sideTasksClaimBadge, .sideBattlePassClaimBadge") as HTMLElement | null;
  if (!badge) {
    badge = document.createElement("span");
    badge.className = "sideTasksClaimBadge sideBattlePassClaimBadge";
    btn.appendChild(badge);
  }
  const show = count > 0;
  badge.textContent = "CLAIM";
  badge.classList.toggle("hidden", !show);
  badge.setAttribute("aria-hidden", show ? "false" : "true");
}
